import { LiquidityScenario, LiquidityScenarioTransaction, TransactionType, TransactionTypeCodes, UpdateLiquidityScenarioHead } from "@/types/liquidity-api";
import { formatNumber } from "@/utils/format";
import { Tooltip } from "@mui/material";
import { UseMutateFunction } from "@tanstack/react-query";
import dayjs from "dayjs";
import { CalendarDaysIcon, ChevronDown, Edit, Save, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/Input";
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { filterActiveTransactions } from "@/utils/transactions";
import { H1, H2, Body } from "@/components/ui/Typography";
import { IconButton } from "../ui/IconButton";
import Card from "../ui/Card";

// Enable the dayJs plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface LiquiditySummaryProps {
	scenario: LiquidityScenario;
	updateLiquidityScenario: UseMutateFunction<LiquidityScenario, Error, {
		liquidityScenarioId: string;
		data: UpdateLiquidityScenarioHead;
	}, unknown>;
}

export const LiquiditySummary = ({ scenario, updateLiquidityScenario }: LiquiditySummaryProps) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [startAmount, setStartAmount] = useState(scenario?.openingBalance || 0);
	const [isEditingStartAmount, setIsEditingStartAmount] = useState(false);

	const transactions = useMemo(() => {
		const dateFrom = dayjs(scenario.dateFrom);
		const dateTo = dayjs(scenario.dateTo);
		return filterActiveTransactions(scenario?.transactions || [], dateFrom, dateTo)
	}, [scenario]);

	const handleSave = () => {
		const data: UpdateLiquidityScenarioHead = {
			name: scenario.name,
			openingBalance: startAmount
		};
		updateLiquidityScenario({ liquidityScenarioId: scenario.id, data });
		setIsEditingStartAmount(false);
	};

	const totalInflows = useMemo(() => {
		return transactions
			?.filter(t => t.transactionType === TransactionType.Invoice)
			.reduce((sum, t) => sum + t.amount, 0) || 0;
	}, [transactions]);

	const totalOutflows = useMemo(() => {
		return transactions
			?.filter(t => t.transactionType === TransactionType.SupplierInvoice)
			.reduce((sum, t) => sum + t.amount, 0) || 0;
	}, [transactions]);

	const closingBalance = useMemo(() => {
		return (scenario?.openingBalance || 0) + totalInflows + totalOutflows;
	}, [scenario, totalInflows, totalOutflows]);

	const incomingTransactionCodeTypeAmounts = useMemo(() => {
		return transactions
			?.filter(t => t.transactionType && t.transactionType === TransactionType.Invoice)
			?.reduce((acc, t) => {
				acc[t.transactionTypeCode] = acc[t.transactionTypeCode] || [];
				acc[t.transactionTypeCode].push(t);
				return acc;
			}, {} as Record<string, LiquidityScenarioTransaction[]>);
	}, [transactions]);

	const outgoingTransactionCodeTypeAmounts = useMemo(() => {
		return transactions
			?.filter(t => t.transactionType && t.transactionType === TransactionType.SupplierInvoice)
			?.reduce((acc, t) => {
				acc[t.transactionTypeCode] = acc[t.transactionTypeCode] || [];
				acc[t.transactionTypeCode].push(t);
				return acc;
			}, {} as Record<string, LiquidityScenarioTransaction[]>);
	}, [transactions]);

	const { highestBalance, highestBalanceDate, lowestBalance, lowestBalanceDate } = useMemo(() => {

		type BalanceExtremes = {
			highestBalance: number;
			highestBalanceDate: Date | null;
			lowestBalance: number;
			lowestBalanceDate: Date | null;
			currentBalance: number;
		};

		const initialBalance = scenario?.openingBalance || 0;
		const initialState: BalanceExtremes = {
			highestBalance: initialBalance,
			highestBalanceDate: scenario.dateFrom,
			lowestBalance: initialBalance,
			lowestBalanceDate: scenario.dateFrom,
			currentBalance: initialBalance
		};

		const { highestBalance, highestBalanceDate, lowestBalance, lowestBalanceDate, currentBalance } = scenario?.transactions
			?.sort((a, b) => dayjs(a.transactionDate).unix() - dayjs(b.transactionDate).unix())
			?.reduce((acc: BalanceExtremes, transaction) => {
				const newBalance = acc.currentBalance + transaction.amount;

				if (newBalance > acc.highestBalance) {
					return {
						highestBalance: newBalance,
						highestBalanceDate: transaction.transactionDate,
						lowestBalance: acc.lowestBalance,
						lowestBalanceDate: acc.lowestBalanceDate,
						currentBalance: newBalance
					};
				} else if (newBalance < acc.lowestBalance) {
					return {
						highestBalance: acc.highestBalance,
						highestBalanceDate: acc.highestBalanceDate,
						lowestBalance: newBalance,
						lowestBalanceDate: transaction.transactionDate,
						currentBalance: newBalance
					};
				} else {
					return acc;
				}
			}, initialState) ?? initialState;

		return {
			highestBalance,
			highestBalanceDate,
			lowestBalance,
			lowestBalanceDate
		};
	}, [scenario]);

	return (
		<Card className="border border-palette-dark-gray rounded-lg" hoverable={false}>
			<div >
				<div className="flex items-center justify-between p-4">
					<H1>{scenario.name || "Översikt"}</H1>
					<div className="flex gap-2 items-center">
						<CalendarDaysIcon className="text-secondary" />
						<Body size="medium" className="text-secondary">
							{dayjs(scenario.dateFrom).format("YYYY-MM-DD")} - {dayjs(scenario.dateTo).format("YYYY-MM-DD")}
						</Body>
					</div>
				</div>
				<div className="grid grid-cols-4 divide-x divide-palette-dark-gray pt-4">
					<div className="flex flex-col">
						<div className="flex justify-between pr-4">
							<div className="pl-4 pr-4">
								<H2 className="mb-2">Ingående balans</H2>
								{isEditingStartAmount ? (
									<Input
										type="number"
										value={startAmount}
										onChange={(e) => setStartAmount(Number(e.target.value))}
										slotProps={{
											input: {
												inputMode: "numeric",
											}
										}}
									/>
								) : (
									<Body size="large">
										{formatNumber(startAmount)} kr
									</Body>
								)}
							</div>
							<div className="flex flex-col justify-end">
								{isEditingStartAmount ? (
									<div className="flex pb-2">
										<IconButton
											color="success"
											onClick={handleSave}>
											<Save className="h-4 w-4" />
										</IconButton>
										<IconButton
											color="error"
											onClick={() => {
												setStartAmount(scenario.openingBalance)
												setIsEditingStartAmount(false)
											}}>
											<X className="h-4 w-4" />
										</IconButton>
									</div>
								) : (
									<div className="">
										<Tooltip title="Redigera ingående balans">
											<IconButton size="small" color="primary" onClick={() => setIsEditingStartAmount(true)}>
												<Edit className="h-4 w-4" />
											</IconButton>
										</Tooltip>
									</div>
								)}
							</div>
						</div>
					</div>
					<div className="pl-4 pr-4">
						<H2 className="mb-2">Inbetalningar</H2>
						<Body size="large">
							{formatNumber(totalInflows)} kr
						</Body>
					</div>
					<div className="pl-4 pr-4">
						<H2 className="mb-2">Utbetalningar</H2>
						<Body size="large">
							{formatNumber(totalOutflows)} kr
						</Body>
					</div>
					<div className="pl-4 pr-4">
						<H2 className="mb-2">Utgående balans</H2>
						<Body size="large">
							{formatNumber(closingBalance)} kr
						</Body>
					</div>
				</div>

				{isExpanded && (
					<div className="grid grid-cols-4 divide-x divide-palette-dark-gray cursor-pointer" onClick={() => setIsExpanded(false)}>
						<div />
						<div className="pl-4 pt-4 pr-4">
							{Object.entries(incomingTransactionCodeTypeAmounts).map(([codeType, transactions]) => {
								const codeLabel = Object.values(TransactionTypeCodes).find(code => code.value === codeType)?.label || ''
								const amount = transactions.reduce((sum, t) => sum + t.amount, 0)
								return (
									<div key={codeType} className="flex justify-between pt-2">
										<Body size="small">{codeLabel}</Body>
										<Body size="small" className="font-semibold">
											{formatNumber(amount)} kr
										</Body>
									</div>
								)
							})}
						</div>
						<div className="pl-4 pt-4 pr-4">
							{Object.entries(outgoingTransactionCodeTypeAmounts).map(([codeType, transactions]) => {
								const codeLabel = Object.values(TransactionTypeCodes).find(code => code.value === codeType)?.label || ''
								const amount = transactions.reduce((sum, t) => sum + t.amount, 0)
								return (
									<div key={codeType} className="flex justify-between pt-2">
										<Body size="small">{codeLabel}</Body>
										<Body size="small" className="font-semibold">
											{formatNumber(amount)} kr
										</Body>
									</div>
								)
							})}
						</div>
						<div className="pl-4 pt-4 pr-4">
							<div className="flex justify-between pt-2">
								<Body size="small">Högst | {dayjs(highestBalanceDate).format('YYYY-MM-DD')}</Body>
								<Body size="small" className="font-semibold">
									{formatNumber(highestBalance)} kr
								</Body>
							</div>
							<div className="flex justify-between pt-2">
								<Body size="small">Lägst | {dayjs(lowestBalanceDate).format('YYYY-MM-DD')}</Body>
								<Body size="small" className="font-semibold">
									{formatNumber(lowestBalance)} kr
								</Body>
							</div>
						</div>
					</div>
				)}

				<div
					className="flex justify-center p-2 cursor-pointer hover:bg-palette-light transition-colors"
					onClick={() => setIsExpanded(!isExpanded)}
				>
					<ChevronDown className={`h-5 w-5 text-palette-gray transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
				</div>
			</div>
		</Card>
	);
};
